import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { MergeUsersModal } from '@/components/MergeUsersModal';
import { PlatformBadge, platformMeta } from '@/lib/platform';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

interface Detail {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string | null;
  isAdmin: boolean;
  avatarUrl: string | null;
  locale: string;
  onboardingPersona: string | null;
  createdAt: string;
  orgMemberships: { orgId: string; orgName: string; role: string }[];
  createdActivitiesCount: number;
  signupsCount: number;
  ratingsReceivedCount: number;
  signupPlatform: string | null;
  signupClientVersion: string | null;
  clients: {
    platform: string;
    version: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    seenDays: number;
  }[];
  isOrphan: boolean;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-3">
      {children}
    </div>
  );
}

function fullName(d: Detail): string {
  return [d.firstName, d.lastName].filter(Boolean).join(' ') || d.email || d.id.slice(0, 8);
}

export function UserDetailPage() {
  const { id } = useParams({ from: '/users/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api.get<{ data: Detail }>(`/api/admin/users/${id}`).then((r) => r.data),
  });

  const [form, setForm] = useState<Partial<Detail> | null>(null);
  const [showAdminToggle, setShowAdminToggle] = useState(false);
  const [adminConfirmTyped, setAdminConfirmTyped] = useState('');
  const [showMerge, setShowMerge] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ data: Detail }>(`/api/admin/users/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'user', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const del = useMutation({
    mutationFn: (force: boolean) =>
      api.delete<{ data: { ok: true; cascaded: unknown } }>(
        `/api/admin/users/${id}?confirm=DELETE${force ? '&force=true' : ''}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate({ to: '/users' });
    },
  });

  if (isLoading) return <div className="p-4 sm:p-6 lg:p-8 text-zinc-500 text-sm">Загрузка…</div>;
  if (error || !data || !form) return <div className="p-4 sm:p-6 lg:p-8 text-rose-300 text-sm">Ошибка загрузки</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate({ to: '/users' })}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> К списку
      </button>

      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="space-y-2 flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50 truncate">
            {fullName(data)}
          </h1>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-zinc-400">{data.email ?? '—'}</span>
            <span className="text-zinc-700">·</span>
            <span className="font-mono text-xs text-zinc-400">@{data.username ?? '—'}</span>
            {data.isAdmin ? <Badge variant="default">admin</Badge> : null}
          </div>
          <div className="flex items-center gap-3 font-mono text-[11px] text-zinc-500 tabular-nums">
            <span title={data.id}>id: {data.id.slice(0, 8)}…</span>
            <span>·</span>
            <span>создан {new Date(data.createdAt).toLocaleDateString('ru')}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={() => setShowMerge(true)}>Слить</Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive">Удалить</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Удалить пользователя?</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-zinc-400 space-y-3">
                <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-medium">
                  Зависимости
                </div>
                <ul className="space-y-1">
                  <DepRow label="Членств в клубах" value={data.orgMemberships.length} />
                  <DepRow label="Создал игр" value={data.createdActivitiesCount} />
                  <DepRow label="Записей" value={data.signupsCount} />
                  <DepRow label="Получил оценок" value={data.ratingsReceivedCount} />
                </ul>
                <div className={data.isOrphan ? 'text-zinc-400' : 'inline-flex items-center gap-2 text-amber-300'}>
                  {!data.isOrphan && <AlertTriangle className="h-4 w-4" />}
                  {data.isOrphan
                    ? 'Юзер — orphan, удаляется чисто.'
                    : 'Cascade удалит все зависимости. Включи force, чтобы продолжить.'}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={del.isPending}
                  onClick={() => del.mutate(!data.isOrphan)}
                >
                  {data.isOrphan ? 'Удалить' : 'Удалить (force)'}
                </Button>
              </DialogFooter>
              {del.isError && (
                <div className="text-rose-300 text-xs mt-2">
                  {del.error instanceof ApiError ? del.error.message : 'Ошибка удаления'}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <MergeUsersModal
        keepId={data.id}
        keepEmail={data.email}
        open={showMerge}
        onOpenChange={setShowMerge}
      />

      {/* Form card */}
      <Card>
        <CardHeader>
          <CardTitle>Профиль</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <SectionHeading>Идентификация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Имя</Label>
                <Input
                  value={form.firstName ?? ''}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Фамилия</Label>
                <Input
                  value={form.lastName ?? ''}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Username</Label>
                <Input
                  value={form.username ?? ''}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Настройки</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Locale</Label>
                <Select
                  value={form.locale ?? 'ru'}
                  onValueChange={(v) => setForm({ ...form, locale: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ru">ru</SelectItem>
                    <SelectItem value="en">en</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Telegram ID</Label>
                <Input className="font-mono text-xs" value={form.telegramId ?? ''} disabled />
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-center pt-2 border-t border-zinc-900">
            <Button
              onClick={() => save.mutate({
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                username: form.username,
                locale: form.locale,
              })}
              disabled={save.isPending}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            {save.isError && (
              <span className="text-rose-300 text-sm self-center">
                {save.error instanceof ApiError ? save.error.message : 'Ошибка сохранения'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin access card */}
      <Card>
        <CardHeader>
          <CardTitle>Admin-доступ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Badge variant={data.isAdmin ? 'default' : 'secondary'}>
              {data.isAdmin ? 'admin' : 'обычный'}
            </Badge>
            <Dialog open={showAdminToggle} onOpenChange={setShowAdminToggle}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {data.isAdmin ? 'Снять admin' : 'Дать admin'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {data.isAdmin ? 'Снять admin-доступ?' : 'Дать admin-доступ?'}
                  </DialogTitle>
                </DialogHeader>
                <div className="text-sm text-zinc-400 space-y-2">
                  <div>
                    {data.isAdmin
                      ? 'Юзер потеряет доступ ко всей админке.'
                      : 'Юзер получит полный доступ ко всей админке и сможет менять любые данные.'}
                  </div>
                  <Label>Введи <code className="font-mono text-zinc-200">I_GRANT_FULL_ACCESS</code></Label>
                  <Input
                    className="font-mono"
                    value={adminConfirmTyped}
                    onChange={(e) => setAdminConfirmTyped(e.target.value)}
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant={data.isAdmin ? 'outline' : 'destructive'}
                    disabled={adminConfirmTyped !== 'I_GRANT_FULL_ACCESS' || save.isPending}
                    onClick={() => {
                      save.mutate({ isAdmin: !data.isAdmin, confirm: 'I_GRANT_FULL_ACCESS' });
                      setShowAdminToggle(false);
                      setAdminConfirmTyped('');
                    }}
                  >
                    Подтвердить
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Откуда заходит */}
      <Card>
        <CardHeader>
          <CardTitle>Источник</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">Зарегистрирован:</span>
            {data.signupPlatform
              ? <PlatformBadge platform={data.signupPlatform} />
              : <PlatformBadge platform="legacy" />}
            {data.signupClientVersion && (
              <span className="font-mono text-[11px] text-zinc-500">
                v{data.signupClientVersion}
              </span>
            )}
          </div>

          {data.clients.length === 0 ? (
            <div className="text-xs text-zinc-500">Заходов с учётом источника пока не было.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  <th className="text-left font-medium pb-2">Платформа</th>
                  <th className="text-left font-medium pb-2">Версия</th>
                  <th className="text-right font-medium pb-2">Дней</th>
                  <th className="text-right font-medium pb-2">Первый</th>
                  <th className="text-right font-medium pb-2">Последний</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => {
                  const { label, icon: Icon, className } = platformMeta(c.platform);
                  return (
                    <tr key={c.platform} className="border-t border-zinc-800">
                      <td className="py-2">
                        <span className={`inline-flex items-center gap-1.5 ${className}`}>
                          <Icon className="h-3 w-3" />
                          {label}
                        </span>
                      </td>
                      <td className="py-2 font-mono text-[11px] text-zinc-400">
                        {c.version ?? '—'}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums text-zinc-200">
                        {c.seenDays}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums text-zinc-500">
                        {new Date(c.firstSeenAt).toLocaleDateString('ru')}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums text-zinc-400">
                        {new Date(c.lastSeenAt).toLocaleDateString('ru')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Relations card */}
      <Card>
        <CardHeader>
          <CardTitle>Связи</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Создал игр" value={data.createdActivitiesCount} />
            <Stat label="Записей" value={data.signupsCount} />
            <Stat label="Получил оценок" value={data.ratingsReceivedCount} />
            <Stat label="Клубов" value={data.orgMemberships.length} />
          </div>
          {data.orgMemberships.length > 0 && (
            <div className="text-xs text-zinc-500 leading-relaxed">
              {data.orgMemberships.map((m) => `${m.orgName} (${m.role})`).join(', ')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DepRow({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-center justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className="font-mono tabular-nums text-zinc-200">{value}</span>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-zinc-800/80 bg-bg-1 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
        {label}
      </div>
      <div className="mt-1 font-mono text-xl tabular-nums font-medium text-zinc-100">
        {value.toLocaleString('ru')}
      </div>
    </div>
  );
}
